import { useEffect, useMemo, useState } from "react";
import { PrismBanner, PrismButton, PrismDialog, PrismSelect } from "@prism-ui/react";

export interface UnresolvedLogo {
  source_name: string;
  reason: string;
  data: string | null;
  candidates: Array<{ projectId: number; title: string; confidence: number }>;
}

interface LogoMatchStepperProps {
  show: boolean;
  unresolved: UnresolvedLogo[];
  autoAssignedCount: number;
  projectOptions: Array<{ id: number; title: string }>;
  onAssign: (logo: UnresolvedLogo, projectId: number) => Promise<void>;
  onClose: () => void;
}

const REASON_COPY: Record<string, string> = {
  "low-confidence": "No project title looked close enough to this filename.",
  ambiguous: "Two or more projects matched this filename about equally well.",
  "project-already-matched": "The best-matching project already received a logo from this archive.",
  "storage-failed": "The image could not be stored. Re-assign it to try again.",
};

const extensionOf = (name: string) => (/\.([a-z0-9]+)$/i.exec(name)?.[1] || "png").toLowerCase();

const previewSrc = (logo: UnresolvedLogo) =>
  logo.data ? `data:image/${extensionOf(logo.source_name)};base64,${logo.data}` : "";

/**
 * Operator fallback for the bulk logo import. The server auto-assigns everything it is
 * confident about; whatever is left comes through here one at a time, with the image shown
 * next to a project picker that leads with the server's ranked guesses.
 */
const LogoMatchStepper = ({
  show,
  unresolved,
  autoAssignedCount,
  projectOptions,
  onAssign,
  onClose,
}: LogoMatchStepperProps) => {
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (show) {
      setIndex(0);
      setSelected("");
    }
  }, [show]);

  const current = unresolved[index];

  // The server's ranked guesses go first (they are usually right even when they fell below
  // the auto-assign bar), then every other project so nothing is unreachable.
  const options = useMemo(() => {
    if (!current) return [{ value: "", display: "Select a project" }];
    const suggested = current.candidates.filter((candidate) => candidate.confidence > 0);
    const suggestedIds = new Set(suggested.map((candidate) => candidate.projectId));
    return [
      { value: "", display: "Select a project" },
      ...suggested.map((candidate) => ({
        value: String(candidate.projectId),
        display: `${candidate.title} — ${Math.round(candidate.confidence * 100)}% match`,
      })),
      ...projectOptions
        .filter((project) => !suggestedIds.has(project.id))
        .map((project) => ({ value: String(project.id), display: project.title })),
    ];
  }, [current, projectOptions]);

  const advance = () => {
    if (index + 1 >= unresolved.length) {
      onClose();
      return;
    }
    setIndex(index + 1);
    setSelected("");
  };

  const assign = async () => {
    if (!current || !selected) return;
    setBusy(true);
    try {
      await onAssign(current, Number(selected));
      advance();
    } finally {
      setBusy(false);
    }
  };

  return (
    <PrismDialog open={show} close-method="user" onPrismClose={onClose} setAriaLabel="Match logos to projects">
      <span slot="header">Match logos to projects</span>
      <div className="project-fairs-modal-body">
        <PrismBanner tone="caution">
          {autoAssignedCount} logo(s) matched automatically. {unresolved.length} need(s) your decision.
        </PrismBanner>

        {current ? (
          <>
            <p className="project-fairs-stepper-progress">
              Logo {index + 1} of {unresolved.length}
            </p>
            <div className="project-fairs-stepper-body">
              <div className="project-fairs-stepper-preview">
                {previewSrc(current) ? (
                  <img src={previewSrc(current)} alt={`Logo from ${current.source_name}`} />
                ) : (
                  <p>No preview available.</p>
                )}
                <small>{current.source_name}</small>
              </div>
              <div className="project-fairs-stepper-choice">
                <p className="project-fairs-field-help">{REASON_COPY[current.reason] || "Needs review."}</p>
                <PrismSelect
                  label="Assign to project"
                  value={selected}
                  options={options}
                  onPrismChange={(e: CustomEvent<{ value?: string }>) => setSelected(e.detail?.value || "")}
                />
                <p className="project-fairs-field-help">
                  The uploaded filename is discarded — the logo is saved under the project&apos;s own name.
                </p>
              </div>
            </div>
          </>
        ) : (
          <p>Every logo has been handled.</p>
        )}
      </div>

      <PrismButton slot="footer" tone="secondary" display="outline" disabled={busy} onPrismClick={advance}>
        Skip this logo
      </PrismButton>
      <PrismButton
        slot="footer"
        tone="primary-brand"
        display="fill"
        disabled={busy || !selected}
        loading={busy}
        onPrismClick={assign}
      >
        Assign &amp; continue
      </PrismButton>
    </PrismDialog>
  );
};

export default LogoMatchStepper;
